import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft2, Cup } from "iconsax-react";
import styled from "styled-components";

import MobileLayout from "../../components/MobileLayout";
import { Md3Button } from "../../shared/components";
import { useGamification } from "../../hooks/useGamification";
import { shuffle, type GameWord } from "../../hooks/useGameWords";
import {
  CompleteCard,
  Feedback,
  GamePageRoot,
  GameTitle,
  GameTopBar,
  ProgressText,
  ScoreBadge,
} from "./gameStyles";

type Props = { words: GameWord[]; onExit?: () => void };

const PuzzleGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 8px;
  width: 100%;
  max-width: 360px;
`;

const Tile = styled.button<{ $selected?: boolean }>`
  aspect-ratio: 1;
  border-radius: 12px;
  border: 2px solid
    ${({ $selected, theme }) =>
      $selected ? theme.colors.primary : theme.colors.border};
  overflow: hidden;
  padding: 0;
  cursor: pointer;
  background: ${({ theme }) => theme.colors.surfaceSecondary};
  position: relative;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

const SignPuzzleGame: React.FC<Props> = ({ words, onExit }) => {
  const navigate = useNavigate();
  const { addPoints } = useGamification();

  const rounds = useMemo(() => {
    return words
      .filter((w) => w.image)
      .slice(0, 5)
      .map((w) => {
        const positions = [0, 1, 2, 3];
        return {
          word: w,
          order: shuffle(positions),
          target: positions,
        };
      });
  }, [words]);

  const [index, setIndex] = useState(0);
  const [order, setOrder] = useState<number[]>(() => rounds[0]?.order || []);
  const [selected, setSelected] = useState<number | null>(null);
  const [score, setScore] = useState(0);
  const [checked, setChecked] = useState(false);
  const [done, setDone] = useState(false);

  const current = rounds[index];
  const isCorrect = order.length === 4 && order.every((v, i) => v === i);

  React.useEffect(() => {
    if (rounds[index]) {
      setOrder(rounds[index].order);
      setSelected(null);
      setChecked(false);
    }
  }, [index, rounds]);

  const swap = (i: number) => {
    if (checked) return;
    if (selected == null) {
      setSelected(i);
      return;
    }
    if (selected === i) {
      setSelected(null);
      return;
    }
    const next = [...order];
    [next[selected], next[i]] = [next[i], next[selected]];
    setOrder(next);
    setSelected(null);
  };

  const handleCheck = async () => {
    setChecked(true);
    if (order.every((v, i) => v === i)) {
      setScore((s) => s + 1);
      await addPoints("GAME_MEMORY");
    }
  };

  const handleNext = () => {
    if (index + 1 >= rounds.length) {
      setDone(true);
      return;
    }
    setIndex((i) => i + 1);
  };

  const positions = [
    { x: "0%", y: "0%" },
    { x: "50%", y: "0%" },
    { x: "0%", y: "50%" },
    { x: "50%", y: "50%" },
  ];

  return (
    <MobileLayout
      headerTitle="Quebra-cabeça do Sinal"
      headerType="private"
      showBannerAd={false}
    >
      <GamePageRoot>
        <GameTopBar>
          <Md3Button
            title="Voltar"
            variant="text"
            onClick={() => (onExit ? onExit() : navigate("/games"))}
            icon={<ArrowLeft2 size={18} />}
          />
          <ScoreBadge>
            <Cup size={16} variant="Bold" />
            {score}
          </ScoreBadge>
        </GameTopBar>

        {done || !current ? (
          <CompleteCard>
            <GameTitle>{done ? "Puzzle completo!" : "Sem imagens"}</GameTitle>
            <Md3Button
              title="Voltar aos jogos"
              onClick={() => navigate("/games")}
            />
          </CompleteCard>
        ) : (
          <>
            <ProgressText>
              Monte a imagem de <strong>{current.word.palavra}</strong> (
              {index + 1}/{rounds.length})
            </ProgressText>
            <PuzzleGrid>
              {order.map((piece, slot) => {
                const pos = positions[piece];
                return (
                  <Tile
                    key={`piece-${current.word.uid}-${piece}`}
                    type="button"
                    $selected={selected === slot}
                    onClick={() => swap(slot)}
                  >
                    <img
                      src={current.word.image}
                      alt=""
                      style={{
                        objectPosition: `${pos.x} ${pos.y}`,
                        transform: "scale(2)",
                        transformOrigin: `${pos.x} ${pos.y}`,
                      }}
                    />
                  </Tile>
                );
              })}
            </PuzzleGrid>
            {!checked ? (
              <Md3Button title="Verificar" onClick={handleCheck} />
            ) : (
              <>
                <Feedback $ok={isCorrect}>
                  {isCorrect ? "Imagem montada!" : "Ainda não está certo"}
                </Feedback>
                <Md3Button title="Próxima" onClick={handleNext} />
              </>
            )}
          </>
        )}
      </GamePageRoot>
    </MobileLayout>
  );
};

export default SignPuzzleGame;
